import React, { useMemo } from 'react';

/**
 * PipelineFunnelCards
 * The 6-column task status pipeline row.
 * Each card maps to one of the 6 task statuses.
 *
 * Props:
 *  - summary    : { [status]: count }  — task counts from API
 *  - tasks      : full task array — used to derive hrs and assignee avatars client-side
 *  - activeFilter, onFilterByStatus
 */

// ── Helpers ──────────────────────────────────────────────────────────────────

/** Convert milliseconds to whole hours, rounded down. */
const msToHrs = (ms) => Math.floor((ms || 0) / 3_600_000);

/**
 * Avatar colour palette — deterministic per assignee name so the same
 * person always gets the same colour across re-renders.
 */
const AVATAR_COLORS = [
    '#28b5fd', '#03488A', '#10b981', '#f59e0b',
    '#8b5cf6', '#ef4444', '#ec4899', '#06b6d4',
];
const avatarColor = (name = '') => {
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
    return AVATAR_COLORS[hash % AVATAR_COLORS.length];
};

// ── Static per-status card configuration ─────────────────────────────────────

const PIPELINE_CONFIG = [
    {
        key: 'Backlog',
        label: 'Backlog',
        borderColor: 'border-slate-300',
        badgeBg: 'bg-slate-100 text-slate-600',
        // The big hrs value: estimated effort (estimatedTimeMs) for backlog items
        hrsField: 'estimated',
        subLabel: 'Estimated Effort',
        highlight: false,
    },
    {
        key: 'In Progress',
        label: 'In Progress',
        borderColor: 'border-[#00AAF1]',
        badgeBg: null, // brand-gradient badge
        // Shows "actual / estimated hrs" format
        hrsField: 'progress',
        subLabel: 'Effort Recorded',
        highlight: true,
    },
    {
        key: 'Under Review',
        label: 'Under Review',
        borderColor: 'border-amber-300',
        badgeBg: 'bg-amber-50 text-amber-600',
        hrsField: 'estimated',
        subLabel: 'Pending Approval',
        highlight: false,
    },
    {
        key: 'Completed',
        label: 'Completed',
        borderColor: 'border-emerald-400',
        badgeBg: 'bg-emerald-50 text-emerald-600',
        // Completed: show actual time logged — "Total Efficiency"
        hrsField: 'actual',
        subLabel: 'Total Efficiency',
        highlight: false,
    },
    {
        key: 'On Hold',
        label: 'On Hold',
        borderColor: 'border-orange-300',
        badgeBg: 'bg-orange-50 text-orange-500',
        // On Hold: show actual time already spent — "Blocked Items"
        hrsField: 'actual',
        subLabel: 'Blocked Items',
        highlight: false,
    },
    {
        key: 'Cancelled',
        label: 'Cancelled',
        borderColor: 'border-red-300',
        badgeBg: 'bg-red-50 text-red-500',
        // Cancelled: show estimated effort that was lost — "Loss Estimate"
        hrsField: 'estimated',
        subLabel: 'Loss Estimate',
        highlight: false,
    },
];

// ── PipelineCard ──────────────────────────────────────────────────────────────

function PipelineCard({ config, count, hrsData, avatars, onClick, isActive }) {
    const { label, borderColor, badgeBg, hrsField, subLabel, highlight } = config;

    // Build the hrs display string
    const hrsDisplay = hrsField === 'progress'
        ? `${hrsData.actual}/${hrsData.estimated}`
        : String(hrsField === 'actual' ? hrsData.actual : hrsData.estimated);

    return (
        <div
            onClick={onClick}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && onClick()}
            aria-pressed={isActive}
            className={`
                bg-white dark:bg-[#1f1f1f] p-5 rounded-2xl border-t-4 ${borderColor}
                border-x border-b border-[#e7e8e9] dark:border-[#303030]
                shadow-[0px_8px_24px_rgba(25,28,29,0.06)] cursor-pointer select-none
                transition-all duration-200 hover:shadow-[0px_8px_32px_rgba(25,28,29,0.14)]
                hover:-translate-y-0.5 active:scale-[0.98] h-full flex flex-col
                ${isActive ? 'ring-2 ring-[#03488a] ring-offset-1' : ''}
                ${highlight ? 'relative overflow-hidden' : ''}
            `}
        >
            {/* Decorative accent bubble for In Progress */}
            {highlight && (
                <div className="absolute top-0 right-0 w-14 h-14 bg-[#00AAF1]/10 rounded-bl-full pointer-events-none" />
            )}

            {/* ── Row 1: label + task-count badge ── */}
            <div className="flex justify-between items-start mb-3 gap-1">
                <span className={`font-semibold text-[13px] leading-tight whitespace-nowrap ${highlight ? 'text-[#03488A] dark:text-[#28b5fd]' : 'text-slate-500 dark:text-slate-400'}`}>
                    {label}
                </span>
                {badgeBg ? (
                    <span className={`${badgeBg} dark:bg-slate-700 dark:text-slate-300 text-[11px] font-bold px-2 py-0.5 rounded-full tabular-nums`}>
                        {String(count).padStart(2, '0')}
                    </span>
                ) : (
                    /* Brand-gradient badge for In Progress */
                    <span
                        className="text-white text-[11px] font-bold px-2 py-0.5 rounded-full tabular-nums"
                        style={{ background: 'linear-gradient(90deg, #03488A 0%, #00AAF1 100%)' }}
                    >
                        {String(count).padStart(2, '0')}
                    </span>
                )}
            </div>

            {/* ── Row 2: big hours value ── */}
            <div className="mb-1">
                <div className={`font-black font-headline leading-none tabular-nums ${highlight ? 'text-[#003263] dark:text-[#a7c8ff]' : 'text-[#191c1d] dark:text-white'}`}>
                    <span className="text-[22px]">{hrsDisplay}</span>
                    <span className="text-sm font-semibold text-[#424750] dark:text-slate-400 ml-1">hrs</span>
                </div>
            </div>

            {/* ── Row 3: sub-label ── */}
            <p className="text-[11px] text-[#7a7f8a] dark:text-slate-500 font-medium mb-4 leading-tight">
                {subLabel}
            </p>


        </div>
    );
}

// ── Main export ───────────────────────────────────────────────────────────────

export default function PipelineFunnelCards({ summary = {}, tasks = [], activeFilter, onFilterByStatus }) {

    /**
     * Derive per-status hours and unique assignee names.
     * Uses pre-aggregated summary.hours from backend if available (accurate across all pages),
     * otherwise computes from the local tasks array.
     */
    const { hrsMap, avatarMap } = useMemo(() => {
        const hrsMap = {};
        const avatarMap = {};

        PIPELINE_CONFIG.forEach(({ key }) => {
            hrsMap[key] = summary?.hours?.[key] || { estimated: 0, actual: 0 };
            avatarMap[key] = new Set();
        });

        tasks.forEach((task) => {
            const rawStatus = task.status;
            const s = ['Closed', 'Close'].includes(rawStatus) ? 'Completed' : rawStatus;
            if (!hrsMap[s]) return;

            // If summary.hours wasn't provided, aggregate locally
            if (!summary?.hours) {
                hrsMap[s].estimated += msToHrs(task.estimatedTimeMs);
                hrsMap[s].actual += msToHrs(task.actualTimeMs);
            }

            // Collect unique assignee display names
            const name = task.assigneeName || task.assignee?.name || task.assignee?.fullName || '';
            if (name) avatarMap[s].add(name);
        });

        // Convert Sets to sorted arrays
        const resolvedAvatars = {};
        Object.entries(avatarMap).forEach(([k, set]) => {
            resolvedAvatars[k] = [...set].sort();
        });

        return { hrsMap, avatarMap: resolvedAvatars };
    }, [tasks, summary?.hours]);

    return (
        <section data-tour="pipeline-funnel" className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 items-stretch">
            {PIPELINE_CONFIG.map((config) => {
                const count = config.key === 'Completed'
                    ? ((summary?.result?.['Completed'] ?? summary['Completed'] ?? 0) + (summary?.result?.['Closed'] ?? summary['Closed'] ?? 0) + (summary?.result?.['Close'] ?? summary['Close'] ?? 0))
                    : (summary?.result?.[config.key] ?? summary[config.key] ?? 0);

                return (
                    <PipelineCard
                        key={config.key}
                        config={config}
                        count={count}
                        hrsData={hrsMap[config.key] || { estimated: 0, actual: 0 }}
                        avatars={avatarMap[config.key] || []}
                        isActive={activeFilter === config.key}
                        onClick={() => onFilterByStatus(activeFilter === config.key ? null : config.key)}
                    />
                );
            })}
        </section>
    );
}
